import { useEffect, useRef, useState } from 'react';
import Layout from '@/features/reservation/components/Layout';
import InfoTradingAgreement from './InfoTradingAgreement';
import TermsAgreement from './TermsAgreement';
import { fetchAuthSession } from 'aws-amplify/auth';
import { NotFound } from '@/components/Pages/Errors/NotFound';
import { eAgreeState, facilityType } from '../type/contract';
import { CircularProgress, Grid } from '@mui/material';
import { useNavigate } from 'react-router';
import { useSearchParams } from 'react-router-dom';
import { notMatchCustomErrorMessage } from '@/lib/sentryClient';
import * as Sentry from '@sentry/react';
import { executeGet, executeGetAtAllResponse } from '@/client/rest';

type Contract = {
  FacilityName: string;
  facility_type: facilityType;
  term_file_name: string;
  term_file_path: string;
  term_contract_id: number;
  info_trade_file_name: string;
  info_trade_file_path: string;
  info_trade_contract_id: number;
  medical_treatment: string;
  terms_and_conditions: string;
  explanation_title: string;
  explanation: string;
  return_instructions: string;
  fee_description: string;
};

type AgreeStatus = {
  TermAgreeStatus: number;
  InfoTradeAgreeStatus: number;
};

export const Agreement = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [page, setPage] = useState<number>(1);
  const [found, setFound] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(true);
  let facilityId: string | undefined = '';
  let cognitoid: string | undefined = '';
  // 契約書内容
  const [contract, setContract] = useState<Contract | null>(null);
  // 契約同意状態ステータス
  const termStatus = useRef<number | null>(null);
  const infoStatus = useRef<number | null>(null);
  function setTermStatus(state: number) {
    termStatus.current = state;
  }
  function setInfoStatus(state: number) {
    infoStatus.current = state;
  }

  // 同意判定取得
  const ContractJudgement = async () => {
    const agreeStatus = await executeGet<AgreeStatus>({
      apiName: 'medirecoWebApi',
      path: '/get-agree-status',
      options: {
        queryParams: {
          id: await sessionStorage['facility_id'],
          customerId: (await fetchAuthSession()).userSub!
        } // 施設ID仮置き
      }
    });
    setTermStatus(agreeStatus?.TermAgreeStatus);
    infoStatus.current = agreeStatus?.InfoTradeAgreeStatus;
    //setInfoStatus(agreeStatus.InfoTradeAgreeStatus);
    if (agreeStatus?.TermAgreeStatus === eAgreeState.agree) nextTrans();
  };

  // 契約内容取得
  const ContractInformation: () => Promise<void> = async () => {
    // 同意状態判定
    await ContractJudgement();
    // Session Storage から施設選択で選択した施設IDと施設名を取得する
    facilityId = await sessionStorage['facility_id'];
    // cognitoID の取得
    const session = await fetchAuthSession();
    cognitoid = session.userSub;
    if (cognitoid === undefined || facilityId === undefined) {
      setFound(true);
      return;
    }

    // 施設の契約同意内容の取得
    const data = await executeGet<Contract>({
      apiName: 'medirecoWebApi',
      path: '/get-contract-article',
      options: {
        queryParams: { id: facilityId! } // 施設ID仮置き
      }
    });

    setContract(data);
    setLoading(false);
  };

  // 画面切り替え
  const nextTrans = () => {
    if (page >= 2) {
      completeHandler();
    } else {
      setPage(page + 1);
      window.scroll({
        top: 0,
        behavior: 'auto'
      });
    }
  };

  // 2画面完了したら
  const completeHandler = async () => {
    // 同意状態登録（両契約）
    if (
      contract?.term_contract_id === undefined ||
      contract?.info_trade_contract_id === undefined ||
      termStatus.current === null ||
      infoStatus.current === null
    ) {
      console.error('値が不正です。');
    } else {
      try {
        const res = await executeGetAtAllResponse({
          apiName: 'medirecoWebApi',
          path: '/update-agree-status',
          options: {
            queryParams: {
              id: await sessionStorage['facility_id'],
              customerId: (await fetchAuthSession()).userSub!,
              termId: contract?.term_contract_id.toString(),
              infoId: contract?.info_trade_contract_id.toString(),
              termStatus: termStatus.current.toString(),
              infoStatus: infoStatus.current.toString()
            }
          }
        });

        if (res.statusCode === 200) {
          navigate('/auth/mynumber');
        }
      } catch (error) {
        if (error instanceof Error && notMatchCustomErrorMessage(error.name)) {
          Sentry.captureException(error);
        }

        console.error(error);
      }
    }
  };

  useEffect(() => {
    try {
      // 予約更新かどうかの識別
      // クエリからreserveIdが渡されているか確認
      const reserveId = searchParams.get('reserveId');
      if (reserveId !== null) {
        sessionStorage.setItem('updateReserveId', reserveId);
      } else {
        sessionStorage.removeItem('updateReserveId');
      }
      // 問診からの遷移
      // 予約更新かどうかの識別
      // クエリからfacilityidが渡されているか確認
      const facilityid = searchParams.get('facilityid');
      if (facilityid !== null) {
        sessionStorage.setItem('facility_id', facilityid);
      }
      const facilityname = searchParams.get('facilityname');
      if (facilityname !== null) {
        sessionStorage.setItem('facility_name', facilityname);
      }
      const reserveid = searchParams.get('reserveid');
      if (reserveid !== null) {
        sessionStorage.setItem('updateReserveId', reserveid);
      }
      // 契約内容取得
      ContractInformation();
    } catch (error) {
      console.error(`データの取得に失敗しました。：${error}`);
      setFound(true);
    }
  }, []);

  // 全同意済みの場合
  if (termStatus.current === eAgreeState.agree && infoStatus.current === eAgreeState.agree) {
    completeHandler();
  }

  if (loading) {
    return (
      <Layout title={''}>
        <Grid container justifyContent="center" alignItems="center" style={{ height: '100vh' }}>
          <Grid item xs={12} justifyContent="center" alignItems="center" sx={{ textAlign: 'center' }}>
            <CircularProgress className="text-primary" />
          </Grid>
        </Grid>
      </Layout>
    );
  }

  // データの検索、取得に失敗時
  if (found) {
    return <NotFound />;
  }

  if (page === 1 && termStatus.current !== eAgreeState.agree) {
    // 病院包括規約同意
    return (
      <TermsAgreement
        nextTrans={nextTrans}
        facilityName={contract?.FacilityName!}
        facilityType={contract?.facility_type!}
        medicalTreatment={contract?.medical_treatment!}
        fileName={contract?.term_file_name!}
        filePath={contract?.term_file_path!}
        explanationTitle={contract?.explanation_title!}
        explanation={contract?.explanation!}
        contractJudgeTerms={termStatus.current!}
        setContractJudgeTerms={setTermStatus}
      />
    );
  } else if (page === 2 && infoStatus.current !== eAgreeState.agree) {
    // 情報返却・取引同意
    return (
      <InfoTradingAgreement
        nextTrans={nextTrans}
        facilityName={contract?.FacilityName!}
        termsAndConditions={contract?.terms_and_conditions!}
        fileName={contract?.info_trade_file_name!}
        filePath={contract?.info_trade_file_path!}
        returnInstructions={contract?.return_instructions!}
        feeDescription={contract?.fee_description!}
        contractJudgeInfo={infoStatus.current!}
        setContractJudgeInfo={setInfoStatus}
      />
    );
  }
};

export default Agreement;
